import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { error } from '@asset-metrix/portal-auth';
import { ChangePasswordWidget } from '@asset-metrix/portal-ui';
import { useShallow } from 'zustand/react/shallow';

import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useStore } from '../store';
import { extractErrorMessage } from '../utils/errorMessage';

import styles from './login.module.scss';

type ChangePasswordProps = {
  forcedChange: boolean;
};

export function ChangePassword({ forcedChange }: ChangePasswordProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [generalError, setGeneralError] = useState<string>();
  const [passwordError, setPasswordError] = useState<string>();
  const [logoutSession, session] = useStore(
    useShallow((state) => [state.logoutSession, state.session])
  );

  const { t } = useTranslation();
  useDocumentTitle('Change password');

  const onChangePassword = useCallback(
    async (newPassword: string) => {
      try {
        await session?.changePassword(newPassword);
        if (forcedChange) {
          const redirectUrl = session?.getRedirectUrl({ clear: true });
          if (redirectUrl) {
            // Return URL may point at another app on this host, so it includes
            // a basename this router does not own.
            globalThis.location.assign(redirectUrl);
            return;
          }
          navigate('/');
        } else if (location.key === 'default') {
          navigate('/');
        } else {
          navigate(-1);
        }
      } catch (e: unknown) {
        if (e instanceof error.WrongPasswordFormatError) {
          setPasswordError(e.message);
        } else {
          setGeneralError(extractErrorMessage(e, 'Password change failed'));
        }
      }
    },
    [
      navigate,
      forcedChange,
      location,
      session,
      setPasswordError,
      setGeneralError,
    ]
  );

  return (
    <div className={styles['login__root']}>
      <div className={styles['login__tenant-logo']} />
      <ChangePasswordWidget
        onBackToLogin={
          forcedChange
            ? async () => {
                // Without the logout, the user would immediately get redirected to
                // this scene again.
                await logoutSession();
                navigate('/login');
              }
            : () => {
                if (location.key === 'default') {
                  navigate('/');
                } else {
                  navigate(-1);
                }
              }
        }
        backLabel={
          forcedChange
            ? t('app.changePasswordWidget.backToLogin')
            : t('app.changePasswordWidget.back')
        }
        onSubmit={onChangePassword}
        title={
          forcedChange
            ? t('app.changePasswordWidget.forcedTitle')
            : t('app.changePasswordWidget.title')
        }
        generalError={generalError}
        passwordError={passwordError}
      />
    </div>
  );
}
